'use client';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { InspectionFormData } from './inspectionData';
import { Ship, User, MapPin } from 'lucide-react';

interface Props {
  form: UseFormReturn<InspectionFormData>;
}

const VESSEL_TYPES = ['Bulk Carrier', 'Tanker', 'Container Ship', 'General Cargo', 'VLCC', 'LNG Carrier', 'OBO Carrier'];
const FLAGS = ['Marshall Islands', 'Panama', 'Liberia', 'Bahamas', 'Singapore', 'Hong Kong', 'Malta', 'Cayman Islands', 'Cyprus', 'Greece'];
const CLASS_SOCIETIES = ['Registro Italiano Navale (IACS)', 'Lloyd\'s Register', 'Bureau Veritas', 'DNV GL', 'American Bureau of Shipping', 'ClassNK', 'Korean Register', 'China Classification Society'];
const PI_CLUBS = ['The London P&I Club', 'Gard P&I', 'Skuld', 'West of England', 'North of England', 'Standard P&I', 'Steamship Mutual', 'Japan P&I Club'];
const CUSTOMER_SCOPES = ['Anglo American', 'FMG', 'Rio Tinto', 'Vale', 'BHP', 'Cargill'];

export default function VesselDetailsStep({ form }: Props) {
  const { register, formState: { errors }, watch } = form;
  const inspectionType = watch('inspectionType');

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Vessel Information */}
      <div className="bg-white rounded-xl border p-6 shadow-card" style={{ borderColor: 'hsl(var(--border))' }}>
        <div className="flex items-center gap-2 mb-5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'hsl(218 70% 22% / 0.1)' }}>
            <Ship size={16} style={{ color: 'hsl(var(--primary))' }} />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Vessel Information</h3>
            <p className="text-xs text-muted">Static vessel data as per Certificate of Registry</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <div className="md:col-span-2 xl:col-span-1">
            <label className="block text-xs font-semibold mb-1.5" style={{ color: 'hsl(var(--foreground))' }}>
              Vessel Name <span className="text-red-500">*</span>
            </label>
            <input
              {...register('vesselName', { required: 'Vessel name is required' })}
              className="input-field"
              placeholder="e.g. CAPE SHANGRILA"
            />
            {errors.vesselName && <p className="text-xs mt-1" style={{ color: 'hsl(var(--destructive))' }}>{errors.vesselName.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5">
              IMO Number <span className="text-red-500">*</span>
            </label>
            <input
              {...register('imoNumber', {
                required: 'IMO number is required',
                pattern: { value: /^\d{7}$/, message: 'IMO must be 7 digits' },
              })}
              className="input-field font-mono"
              placeholder="9511454"
              maxLength={7}
            />
            {errors.imoNumber && <p className="text-xs mt-1" style={{ color: 'hsl(var(--destructive))' }}>{errors.imoNumber.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5">Vessel Type <span className="text-red-500">*</span></label>
            <select {...register('vesselType', { required: true })} className="input-field">
              {VESSEL_TYPES.map((t) => <option key={`vtype-${t}`} value={t}>{t}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5">Deadweight (MT) <span className="text-red-500">*</span></label>
            <input
              {...register('deadweight', { required: 'Required' })}
              className="input-field font-mono"
              placeholder="176,189"
            />
            {errors.deadweight && <p className="text-xs mt-1" style={{ color: 'hsl(var(--destructive))' }}>{errors.deadweight.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5">Gross Tonnage <span className="text-red-500">*</span></label>
            <input
              {...register('grossTonnage', { required: 'Required' })}
              className="input-field font-mono"
              placeholder="91,985"
            />
            {errors.grossTonnage && <p className="text-xs mt-1" style={{ color: 'hsl(var(--destructive))' }}>{errors.grossTonnage.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5">Flag <span className="text-red-500">*</span></label>
            <select {...register('flag', { required: 'Flag is required' })} className="input-field">
              <option value="">Select flag state</option>
              {FLAGS.map((f) => <option key={`flag-${f}`} value={f}>{f}</option>)}
            </select>
            {errors.flag && <p className="text-xs mt-1" style={{ color: 'hsl(var(--destructive))' }}>{errors.flag.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5">Classification Society <span className="text-red-500">*</span></label>
            <select {...register('classificationSociety', { required: true })} className="input-field">
              <option value="">Select classification society</option>
              {CLASS_SOCIETIES.map((c) => <option key={`class-${c}`} value={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5">Port of Registry</label>
            <input {...register('portOfRegistry')} className="input-field" placeholder="e.g. MAJURO" />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5">Delivery Date</label>
            <input {...register('deliveryDate')} type="date" className="input-field" />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5">P&I Club</label>
            <select {...register('piClub')} className="input-field">
              <option value="">Select P&I Club</option>
              {PI_CLUBS.map((p) => <option key={`pi-${p}`} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Inspection Details */}
      <div className="bg-white rounded-xl border p-6 shadow-card" style={{ borderColor: 'hsl(var(--border))' }}>
        <div className="flex items-center gap-2 mb-5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'hsl(218 70% 22% / 0.1)' }}>
            <MapPin size={16} style={{ color: 'hsl(var(--primary))' }} />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Inspection Location & Type</h3>
            <p className="text-xs text-muted">Where and how the inspection is being conducted</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold mb-1.5">Port of Inspection <span className="text-red-500">*</span></label>
            <input {...register('portOfInspection', { required: 'Required' })} className="input-field" placeholder="e.g. Qingdao" />
            {errors.portOfInspection && <p className="text-xs mt-1" style={{ color: 'hsl(var(--destructive))' }}>{errors.portOfInspection.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5">Country of Inspection <span className="text-red-500">*</span></label>
            <input {...register('countryOfInspection', { required: 'Required' })} className="input-field" placeholder="e.g. P.R. China" />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5">Date of Inspection <span className="text-red-500">*</span></label>
            <input {...register('dateOfInspection', { required: 'Required' })} type="date" className="input-field" />
            {errors.dateOfInspection && <p className="text-xs mt-1" style={{ color: 'hsl(var(--destructive))' }}>{errors.dateOfInspection.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5">Embarkation Date & Time</label>
            <input {...register('embarkationDateTime')} type="datetime-local" className="input-field" />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5">Disembarkation Date & Time</label>
            <input {...register('disembarkationDateTime')} type="datetime-local" className="input-field" />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5">Customer Scope</label>
            <select {...register('customerScope')} className="input-field">
              {CUSTOMER_SCOPES.map((s) => <option key={`scope-${s}`} value={s}>{s}</option>)}
            </select>
          </div>

          <div className="md:col-span-2 xl:col-span-3">
            <label className="block text-xs font-semibold mb-2">Inspection Type <span className="text-red-500">*</span></label>
            <div className="flex gap-3">
              {(['Full', 'Partial'] as const).map((type) => (
                <label
                  key={`itype-${type}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all duration-150 flex-1 max-w-xs"
                  style={{
                    borderColor: inspectionType === type ? 'hsl(var(--primary))' : 'hsl(var(--border))',
                    background: inspectionType === type ? 'hsl(218 70% 22% / 0.06)' : 'transparent',
                  }}
                >
                  <input {...register('inspectionType')} type="radio" value={type} className="sr-only" />
                  <div className="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                    style={{ borderColor: inspectionType === type ? 'hsl(var(--primary))' : 'hsl(var(--border))' }}>
                    {inspectionType === type && (
                      <div className="w-2 h-2 rounded-full" style={{ background: 'hsl(var(--primary))' }} />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-semibold">{type} Inspection</p>
                    <p className="text-xs text-muted">
                      {type === 'Full' ? 'All 14 categories — Third-party vendors' : 'One category — Port captains only'}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Inspector Details */}
      <div className="bg-white rounded-xl border p-6 shadow-card" style={{ borderColor: 'hsl(var(--border))' }}>
        <div className="flex items-center gap-2 mb-5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'hsl(218 70% 22% / 0.1)' }}>
            <User size={16} style={{ color: 'hsl(var(--primary))' }} />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Inspector Information</h3>
            <p className="text-xs text-muted">Who is conducting this inspection</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold mb-1.5">Inspector Name <span className="text-red-500">*</span></label>
            <input
              {...register('inspectorName', { required: 'Inspector name is required' })}
              className="input-field"
              placeholder="e.g. Song-Qing Zhang"
            />
            {errors.inspectorName && <p className="text-xs mt-1" style={{ color: 'hsl(var(--destructive))' }}>{errors.inspectorName.message}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}